<template>
   <div class="max-w-(--breakpoint-2xl) container mx-auto px-4 md:px-8 pb-8 grow">
      <fa
         :icon="['fad', 'spinner-third']"
         class="z-50 text-dgreen opacity-80 fa-spin mx-auto w-8 h-8 fixed left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2"
         v-if="loading"
      />

      <div class="pb-24 pt-4 md:pt-8 lg:grid lg:grid-cols-3 lg:gap-x-8 xl:grid-cols-4">
         <aside class="relative" v-if="!category.hide_filters">
            <strong class="sr-only">Filters</strong>

            <transition name="fade">
               <div v-if="loading" class="z-50 inset-0 bg-white/50 absolute transition-all"></div>
            </transition>

            <!-- Subcategories -->
            <fieldset v-if="children.length && category.show_children" class="mb-6">
               <legend class="block font-semibold text-gray-900">
                  <Link
                     v-if="category.parent && !category.parent.hide"
                     :href="url(category.parent.slug)"
                     :title="category.parent.title"
                     class="cursor-pointer hover:text-gray-900"
                     >{{ category.parent.title }}
                  </Link>
                  <Link
                     v-else
                     :href="url(category.slug)"
                     :title="category.title"
                     class="cursor-pointer hover:text-gray-900"
                     >{{ category.title }}
                  </Link>
               </legend>
               <div class="pt-2">
                  <ul class="select-none space-y-2 pl-3 border-l">
                     <li v-for="child in children">
                        <Link
                           v-if="category.parent && !category.parent.hide"
                           :href="url(category.parent.slug + '/' + child.slug)"
                           :title="child.title"
                           class="text-gray-600 cursor-pointer hover:text-gray-900"
                           :class="{ 'font-bold': child.slug == category.slug }"
                           >{{ child.title }}</Link
                        >
                        <Link
                           v-else
                           :href="url(category.slug + '/' + child.slug)"
                           :title="child.title"
                           class="text-gray-600 cursor-pointer hover:text-gray-900"
                           >{{ child.title }}</Link
                        >
                     </li>
                  </ul>
               </div>
            </fieldset>

            <!-- Wizard -->
            <div
               class="hidden md:block bg-white rounded-xl shadow-lg border-gray-200 transition -ml-4 mb-10"
               v-if="(category.id == 1 || category.catalog_id == 1) && (wizardCompleted == true || showWizardSmall)"
            >
               <div class="mb-4 px-4 py-6 bg-gray-50 border-dgreen border-t-4 rounded-t-xl">
                  <span class="font-bold">Welke cilinders heeft u nodig?</span>
                  <p class="text-gray-600">Geef hier uw wensen aan en bekijk direct wat de totaalprijs is!</p>
               </div>

               <div
                  v-for="(cylinder, index) in data.query.cylinders"
                  :key="index"
                  class="flex flex-wrap text-sm relative px-4"
               >
                  <!-- Size -->
                  <cylinder-size
                     :options="[10, 30, 35, 40, 45, 50, 60, 70]"
                     v-model:valueA="cylinder.a"
                     v-model:valueB="cylinder.b"
                     :categoryFilter="true"
                     :index="index"
                     class="w-3/5 my-1"
                     :key="refreshKey"
                  />

                  <!-- Buttons -->
                  <buttons
                     :options="[
                        { value: 'Geen', is_active: true },
                        { value: 'Knop', is_active: true },
                        { value: 'Pushknop', is_active: true },
                     ]"
                     :cylinder="cylinder"
                     v-model:button="cylinder.button"
                     :categoryFilter="true"
                     info="<p>Een draaiknop&nbsp;biedt extra gemak, want je hoeft niet telkens een sleutel te gebruiken om de deur te openen voor bijvoorbeeld de postbode.<br>
                                    <br>
                                    <strong>Pushknop</strong><br>
                                    De pushknop werkt hetzelfde als de normale knop alleen moet deze tijdens het draaien worden ingedrukt. Dit is veiliger tegen inbraak maar ook lastiger te bedienen.</p>"
                     class="w-2/5 pl-2 my-1"
                     :index="index"
                  />

                  <!-- Remove cylinder -->
                  <div
                     type="button"
                     class="absolute right-0 top-4 mr-2 inline-flex items-center p-1 border rounded-full shadow border-gray-300 text-gray-500 bg-white hover:bg-gray-100 focus:outline-hidden active:border-gray-400 cursor-pointer group"
                     @click="remove(index)"
                     v-if="index != 0"
                  >
                     <fa :icon="['fal', 'minus']" class="h-3 w-3 text-gray-400" />
                     <span class="tippy">Verwijder cilinder</span>
                  </div>
               </div>

               <!-- Extra cylinder -->
               <div class="w-full relative mt-2" key="plus">
                  <div class="w-full text-center justify-center">
                     <div
                        class="inline-flex items-center p-1.5 border rounded-full shadow border-gray-300 text-gray-500 bg-white hover:bg-gray-100 focus:outline-hidden active:border-gray-400 cursor-pointer group"
                        @click="add()"
                     >
                        <fa :icon="['fal', 'plus']" class="h-4 w-4 text-gray-400" />
                        <span class="tippy">Extra cilinder toevoegen</span>
                     </div>
                  </div>
               </div>

               <!-- Keys -->
               <KeyQty
                  v-model:keysSelected="keysSelected"
                  v-model:keyQty="data.query.key_qty"
                  :default_qty="data.lowestKeyQty"
                  class="mt-4 text-sm px-4"
                  :categoryFilter="true"
               >
                  <!-- <div class="block text-xs font-medium text-gray-700 mb-1">Aantal sleutels</div> -->
               </KeyQty>

               <transition name="fade">
                  <div
                     class="py-3 bg-gray-50 items-center px-4 text-center rounded-b-xl text-gray-600 hover:text-gray-900 group cursor-pointer"
                     @click="resetFilter()"
                     v-if="filtered"
                  >
                     <fa
                        :icon="['fal', 'undo']"
                        class="mr-3 group-hover:-rotate-45 w-4 h-4 transition"
                        v-if="!data.resetLoading"
                     />
                     <fa
                        :icon="['fad', 'spinner-third']"
                        class="opacity-80 mr-3 w-4 h-4 fa-spin"
                        v-if="data.resetLoading"
                     />
                     <span class="inline-block">Reset maten en opties</span>
                  </div>
               </transition>
            </div>

            <!-- <div
                    class="hidden md:block -ml-6 mb-10"
                    v-if="(category.id == 1 || category.catalog_id == 1) && !wizardCompleted && !showWizardSmall"
                >
                    <WizardIntro />
                </div> -->

            <transition name="fade">
               <div v-if="data.activeFilters.length" class="py-2 my-2">
                  <legend class="block mb-2">
                     <span class="font-semibold text-gray-900">
                        <fa :icon="['fas', 'filter-list']" class="h-4 w-4 mr-2" /> Gekozen filters</span
                     >
                     <span @click="resetFilter()" class="link text-sm ml-3">
                        <fa
                           :icon="['fad', 'spinner-third']"
                           class="opacity-80 mr-1 w-4 h-4 fa-spin"
                           v-if="data.resetLoading"
                        />Wis alle filters</span
                     >
                  </legend>

                  <ul class="text-sm">
                     <li v-for="filter in data.activeFilters">
                        <div v-if="filter.type == 'custom'">
                           {{ filter.name
                           }}<fa
                              @click="resetAttribute(filter)"
                              :icon="['fal', 'times']"
                              class="ml-2 h-3 w-3 hover:cursor-pointer hover:text-red-700"
                           />
                        </div>
                        <div v-else v-for="(, id) in filter">
                           {{ getAttributeName(id)
                           }}<fa
                              @click="resetAttribute(filter, id)"
                              :icon="['fal', 'times']"
                              class="ml-2 h-3 w-3 hover:cursor-pointer hover:text-red-700"
                           />
                        </div>
                     </li>
                  </ul>
               </div>
            </transition>

            <!-- Mobile filter dialog -->
            <div class="flex gap-x-4">
               <button
                  class="inline-flex items-center xl:hidden mb-4 button border border-dgreen w-2/3"
                  @click="showWizardFromQty"
                  v-if="category.id == 1 || category.catalog_id == 1"
               >
                  <div class="flex justify-start">
                     <fa :icon="['fas', 'clipboard-list-check']" class="mr-2 w-6 h-6 text-dgreen left-0" />
                  </div>
                  <div class="grow flex flex-col text-left item leading-5">
                     <span>Keuzehulp</span>
                     <span class="text-xs font-medium">In 4 stappen de juiste cilinder</span>
                  </div>
               </button>
               <slide-over pin="right" title="Filters" icon="plus" type="link" ref="menu" :padding="true">
                  <template v-slot:button>
                     <button
                        class="inline-flex items-center lg:hidden mb-4 button default large w-1/3"
                        @click="openMenu()"
                     >
                        <span class="sr-only">Filters</span>
                        <fa :icon="['fal', 'filter-list']" class="mr-2 text-gray-600" />
                        <span v-if="activeFilters.length" class="inline-block mx-2">{{ activeFilters.length }}</span>
                        <span class="font-medium" v-else>Filters</span>
                     </button>
                  </template>
                  <template v-slot:bottom>
                     <div class="fixed bottom-4 w-64 button dgreen mt-6 md:hidden" @click="menu.open = false">
                        Bekijk {{ data.filteredProducts.length }} producten
                     </div>
                  </template>
                  <!-- Cylinder options -->
                  <div v-for="option in options.cylinder_options_default" class="py-3">
                     <checkbox
                        :attribute="option"
                        v-model:value="data.query[option.id]"
                        v-if="category.id == 1 || category.catalog_id == 1"
                        :key="refreshKey"
                        :category="true"
                     ></checkbox>
                  </div>

                  <!-- Filterable filters -->
                  <div class="space-y-2">
                     <div v-for="filter in data.filterable" :key="filter.name">
                        <checkbox
                           :attribute="filter"
                           v-model:value="data.query.attributes"
                           :key="refreshKey"
                           :category="true"
                           :filterbale="true"
                        ></checkbox>
                     </div>
                  </div>
               </slide-over>
            </div>

            <div class="hidden lg:block">
               <div class="space-y-7" ref="filters">
                  <div
                     class="block md:hidden py-3 bg-gray-50 items-center px-4 text-center rounded-b-xl text-gray-600 hover:text-gray-900 group cursor-pointer"
                     @click="resetFilter()"
                     v-if="filtered && activeFilters.length"
                  >
                     <fa :icon="['fal', 'undo']" class="mr-3 group-hover:-rotate-45 w-4 h-4 transition" />
                     <span class="inline-block">Reset maten en opties</span>
                  </div>

                  <div v-if="category.id == 1 || category.catalog_id == 1 || category.catalog_id == 73">
                     <fieldset>
                        <legend class="block font-semibold text-gray-900">
                           <span>Gelijksluitende sets</span>
                        </legend>
                        <div class="pt-2">
                           <Link
                              v-if="category.catalog_id != 73"
                              :href="url('cilindersloten')"
                              class="-ml-2 px-2 py-1 rounded-lg text-gray-600 cursor-pointer hover:text-gray-900 hover:bg-gray-50 block"
                              :class="{
                                 ' bg-gray-100': data.query.cylinders && data.query.cylinders.length == 1,
                              }"
                           >
                              1 cilinder
                           </Link>
                           <Link
                              v-else
                              title="Cilinders met smart lock"
                              :href="url('smartlocks/cilinder-met-smart-lock')"
                              class="-ml-2 px-2 py-1 rounded-lg text-gray-600 cursor-pointer hover:text-gray-900 hover:bg-gray-50 block"
                              >1 cilinder</Link
                           >

                           <Link
                              v-if="category.catalog_id == 73"
                              v-for="n in [2, 3, 4, 5, 6, 7]"
                              :href="'/smartlocks/cilinder-met-smart-lock/' + n + '-cilinders'"
                              class="-ml-2 px-2 py-1 rounded-lg text-gray-600 cursor-pointer hover:text-gray-900 hover:bg-gray-50 block"
                              :class="{
                                 ' bg-gray-100': data.query.cylinders && data.query.cylinders.length == n,
                              }"
                           >
                              {{ n }} cilinders
                           </Link>
                           <Link
                              v-else
                              v-for="n in [2, 3, 4, 5, 6, 7]"
                              :href="url('cilindersloten/sets/' + n + '-stuks')"
                              class="-ml-2 px-2 py-1 rounded-lg text-gray-600 cursor-pointer hover:text-gray-900 hover:bg-gray-50 block"
                              :class="{
                                 ' bg-gray-100': data.query.cylinders && data.query.cylinders.length == n,
                              }"
                           >
                              {{ n }} cilinders
                           </Link>
                           <button @click="showWizardFromQty" class="link" v-if="category.catalog_id != 73">
                              Meer cilinders
                           </button>
                        </div>
                     </fieldset>
                  </div>

                  <!-- Cylinder options -->
                  <div v-for="option in options.cylinder_options_default">
                     <checkbox
                        :attribute="option"
                        v-model:value="data.query[option.id]"
                        v-if="category.id == 1 || category.catalog_id == 1"
                        :key="refreshKey"
                        :category="true"
                     ></checkbox>
                  </div>

                  <!-- Filterable filters -->
                  <div class="space-y-2">
                     <div v-for="filter in data.filterable" :key="filter.name">
                        <checkbox
                           :attribute="filter"
                           v-model:value="data.query.attributes"
                           :key="refreshKey"
                           :category="true"
                           :filterbale="true"
                        ></checkbox>
                     </div>
                  </div>
               </div>
            </div>
         </aside>

         <section
            aria-labelledby="product-heading"
            :class="category.hide_filters ? 'lg:col-span-3 xl:col-span-4' : 'lg:col-span-2 xl:col-span-3'"
            class="lg:col-span-2 xl:col-span-3 overflow-hidden relative"
         >
            <nav class="flex justify-between pb-3 -mx-2">
               <div class="flex justify-between grow w-full">
                  <!-- Top filters -->
                  <div
                     v-for="filter in brandFilters"
                     :key="filter.name"
                     class="px-2 w-full gap-3 flex flex-row justify-between"
                     v-if="category.id == 2 && category.title != 'Retail'"
                  >
                     <TopFilterButton
                        :attribute="filter"
                        v-model:value="data.query.attributes"
                        :key="refreshKey"
                        :category="true"
                        :filterbale="true"
                     ></TopFilterButton>
                  </div>

                  <div class="hidden md:flex grow w-full justify-between" v-if="topCategories.length">
                     <Link
                        v-for="topCategory in topCategories"
                        :href="topCategory.url"
                        class="w-full px-2"
                        :title="topCategory.name"
                        :class="{ 'border-dgreen': topCategory.slug == category.slug }"
                        ><div
                           class="border items-center cursor-pointer rounded-md flex flex-wrap justify-center md:justify-start font-medium p-2 hover:shadow transition h-14"
                        >
                           <VuePicture
                              :image="topCategory.image"
                              :style="'h-10 mx-auto'"
                              :name="topCategory.name"
                              :width="50"
                              :height="40"
                              class="mr-4 w-full md:w-auto"
                           />
                           <span
                              v-if="[22, 23].includes(category.id)"
                              class="text-center w-full md:w-auto"
                              :class="{ 'ml-2': !topCategory.image }"
                              >{{ topCategory.name.split(' ')[0] + ' ' + topCategory.name.split(' ')[1] }}</span
                           >
                           <span v-else class="text-center w-full md:w-auto" :class="{ 'ml-2': !topCategory.image }">{{
                              topCategory.name
                           }}</span>
                        </div>
                     </Link>
                  </div>

                  <div
                     class="px-2 w-full md:w-auto"
                     v-if="category.title && category.title.toLowerCase().includes('nabestellen')"
                  >
                     <Link
                        class="border items-center cursor-pointer rounded-md flex flex-wrap justify-center md:justify-start font-medium p-2 hover:shadow transition h-14 w-full md:w-48"
                        :href="url('veiligheidscertificaat-aanvragen')"
                     >
                        <div class="relative">
                           <fa :icon="['fal', 'credit-card']" class="mx-2 h-6 w-6 text-dgreen" />
                           <fa
                              :icon="['fas', 'shield-check']"
                              class="mx-2 h-4 w-4 text-dgreen absolute -right-1 top-0 bg-white"
                           />
                        </div>
                        <span>Certificaat kwijt?</span>
                     </Link>
                  </div>
               </div>
            </nav>

            <div class="border-gray-200 rounded-lg border">
               <Transition name="fade">
                  <div v-if="loading" class="z-50 inset-0 bg-white/50 absolute transition-all"></div>
               </Transition>
               <div class="border-b border-gray-200 px-6 py-4 flex bg-white rounded-t-lg">
                  <span id="product-heading" class="sr-only">Products</span>
                  <div class="col-start-2 my-2 w-1/2">
                     {{ data.filteredProducts.length }} resultaten

                     <div
                        class="button dgreen block ml-10"
                        @click="resetFilter()"
                        v-if="data.filteredProducts.length == 0"
                     >
                        <fa :icon="['fal', 'undo']" class="mr-3 group-hover:-rotate-45 w-4 h-4 transition" />
                        <span class="inline-block">Reset filters</span>
                     </div>
                  </div>

                  <div class="w-64" v-if="category.title == 'Retail'">
                     <input type="search" v-model="data.query.search" placeholder="Zoek op model" class="text-input" />
                  </div>

                  <div class="text-right w-1/2 z-10 relative">
                     <Menu as="div" class="relative inline-block text-left">
                        <MenuButton
                           class="inline-flex justify-center w-full rounded-md border border-gray-300 shadow px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-indigo-500"
                        >
                           {{ data.query.sortBy.name }}
                           <fa :icon="['fal', 'angle-down']" class="-mr-1 ml-2 h-5 w-5" />
                        </MenuButton>

                        <transition name="drop">
                           <MenuItems
                              class="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black/10 focus:outline-hidden"
                           >
                              <MenuItem v-for="option in sortOptions" :key="option.name" v-slot="{ active }">
                                 <a
                                    @click="data.query.sortBy = option"
                                    :class="[
                                       option.value == data.query.sortBy.value
                                          ? 'font-medium text-gray-900'
                                          : 'text-gray-500',
                                       active ? 'bg-gray-100' : '',
                                       'block px-4 py-3 text-sm',
                                    ]"
                                 >
                                    {{ option.name }}
                                 </a>
                              </MenuItem>
                           </MenuItems>
                        </transition>
                     </Menu>
                  </div>
               </div>

               <List
                  v-if="category.id == 1 || category.catalog_id == 1 || category.id == 16 || category.id == 84"
                  :products="data.filteredProducts"
                  :category="category"
                  :filtered="filtered"
                  :wizardUsed="data.wizardUsed"
               />

               <Grid
                  v-else
                  :products="data.filteredProducts"
                  :category="category"
                  :filtered="filtered"
                  :wizardUsed="data.wizardUsed"
               />
            </div>
         </section>
      </div>
   </div>
</template>

<script setup>
import Buttons from '@/Components/Configuration/Legacy/Buttons.vue';
import CylinderSize from '@/Components/Configuration/Legacy/CylinderSize.vue';
import SlideOver from '@/Components/SlideOver.vue';
import TopFilterButton from '@/Components/TopFilterButton.vue';
import Product from '@/Models/Product';
import Grid from '@/Pages/Catalog/CategoryProducts.vue';
import List from '@/Pages/Catalog/CategoryProductsList.vue';
import { router, useForm, usePage } from '@inertiajs/vue3';
import { computed, onMounted, reactive, ref, watch } from 'vue';

const props = defineProps({
   category: Object,
   children: Object | Boolean,
   products: Object,
   options: Object,
   filter: Object | Boolean,
   counters: Object,
   topCategories: Array,
   showWizardSmall: Boolean,
});

const inBrowser = typeof window !== 'undefined';
const shouldWatch = ref(false);
const reset = ref(false);
var refreshKey = 0;
const loading = ref(false);
const filters = ref(false);
const filtered = ref(props.filter ? true : false);
const keysSelected = ref(false);
const sortOptions = [
   { name: 'Populair', value: 'position' },
   { name: 'Laagste prijs', value: 'price' },
   { name: 'Levertijd', value: 'delivery_days' },
];
const menu = ref(null);
const data = reactive({
   filteredProducts: props.products,
   filteredCounters: [],
   activeFilters: [],
   filterable: [],
   query: {
      // order_column: '-rating',
      skg: [],
      attributes: [],
      rating: [],
      brand: [],
      category: props.category.catalog_id == 1 ? 1 : (props.category.catalog_id ?? props.category.id),
      origin_category: props.category.id,
      color: [],
      cylinders: [],
      key_qty: 0,
      sortBy: sortOptions[0],
      search: '',
   },
   lowestKeyQty: 3,
   resetLoading: false,
   wizardUsed: false,
});

const toSnakeCase = (str) =>
   str &&
   str
      .replace(/[^A-Za-z0-9\-]/, '')
      .replace(/ /g, '')
      .replace(/["("]/g, '')
      .replace(/[")"]/g, '')
      .replace(/[","]/g, '')
      .replace(/["/"]/g, '');

const wizardCompleted = computed(() => usePage().props.wizardCompleted);

const form = useForm({
   category: props.category.id,
});

function getAttributeName(id) {
   return props.options.filterable.find((x) => x.id == id).name;
}

function add() {
   data.query.cylinders.push({
      a: 30,
      b: 30,
      button: { value: 'Geen' },
   });
}

function remove(index) {
   data.query.cylinders.splice(index, 1);
}

function resetFilter(reload = false) {
   data.resetLoading = true;

   form.get('/api/products/resetfilter', {
      reload: reload,
      preserveScroll: true,
   });
}

function openMenu() {
   menu.value.open = true;
}

function fetch() {
   loading.value = true;

   if (props.category.id == 2 && props.category.name == 'Retail') {
      data.query.brand = [1];
   }

   Product.orderBy(data.query.sortBy.value, 'id')
      .whereIn('brand', data.query.brand)
      .whereIn('rating', data.query.rating)
      .whereIn('color', data.query.color)
      .whereIn('skg', data.query.skg)
      .params({
         query: JSON.stringify(data.query).replace('&', ''),
         reset: reset.value,
      })
      .get()
      .then((response) => {
         data.filteredProducts = response[0].products;
         data.filteredCounters = response[0].counters;
         data.activeFilters = response[0].activeFilters;
         data.wizardUsed = response[0].wizardUsed;

         let keys = [];

         response[0].products.filter(function (product) {
            keys.push(product.keys);
         });
         data.lowestKeyQty = Math.min(...keys);

         updateCounters();
         // refreshKey++
      })
      .finally(() => {
         if (!reset.value) {
            filtered.value = true;
         }
         if (reset.value) {
            data.query.attributes = [];
            data.query.brand = [];
            data.query.rating = [];
            data.query.color = [];
            data.query.skg = [];
            data.query.key_qty = false;
         }

         loading.value = false;

         router.remember(JSON.parse(JSON.stringify(data.query)), 'my-filters-' + props.category.id);
      })
      .catch(() => {
         // console.log(error);
      });
}

const activeFilters = computed(() => {
   let attributes = [];

   if (data.query.attributes && data.query.attributes.length) {
      data.query.attributes.forEach((element) => {
         attributes.push(parseFloat(element.split('-')[0]));
      });
   }

   if (data.cylinderOptions) {
      data.cylinderOptions.forEach((element) => {
         if (data.query[element.id].length) {
            attributes.push(element.id);
         }
      });
   }

   if (data.query.cylinders && data.query.cylinders.length > 1) {
      attributes.push('Cylinders');
   }

   if (data.query.key_qty) {
      attributes.push('key_qty');
   }

   return attributes;
});

const brandFilters = computed(() => {
   return data.filterable.filter((x) => x.id == 14);
});

function updateCounters() {
   Object.values(data.filterable).forEach((element) => {
      Object.values(element.values).forEach((value) => {
         value.count = counter(element.id, value.id);
      });
   });

   if (data.cylinderOptions) {
      Object.values(data.cylinderOptions).forEach((element) => {
         Object.values(element.values).forEach((value) => {
            value.count = counter(element.id, value.id);
         });
      });
   }
}

function counter(attributeId, valueId) {
   let value = toSnakeCase(String(valueId));

   let filtered = data.filteredCounters[`${attributeId}-${value}`];

   if (!filtered) {
      if (activeFilters.value.length && activeFilters.value.includes(attributeId)) {
         return props.counters[`${attributeId}-${value}`];
      }
      return 0;
   }

   return filtered;
}

function showWizardFromQty() {
   if (inBrowser) {
      window.mitt.emit('open-wizard', true);
   }
}

function resetAttribute(filter, id = false) {
   if (filter.type == 'custom') {
      data.query[filter.id] = [];
   } else {
      let keys = [];

      data.query.attributes.forEach(function (value, key) {
         let attributeId = value.split('-')[0];
         if (id == attributeId) {
            keys.push(key);
         }
      });

      var filteredArray = data.query.attributes.filter(function (value, key) {
         return keys.indexOf(key) == -1;
      });

      data.query.attributes = filteredArray;
   }
}

watch(
   () => data.query,
   () => {
      if (shouldWatch.value) {
         fetch();
         refreshKey++;
      }
   },
   { deep: true }
);

watch(
   () => data.query.key_qty,
   (val) => {
      if (shouldWatch.value && val > 0) {
         keysSelected.value = true;
      }
   },
   { deep: true }
);

onMounted(() => {
   // Restore filters when navigation back
   const storedFilters = router.restore('my-filters-' + props.category.id);

   if (storedFilters) {
      data.query = reactive(storedFilters);
   } else {
      // Reset the data.query to its initial state if no stored filters arefound
      data.query = {
         skg: [],
         attributes: [],
         rating: [],
         brand: [],
         category: props.category.catalog_id == 1 ? 1 : (props.category.catalog_id ?? props.category.id),
         origin_category: props.category.id,
         color: [],
         cylinders: [],
         key_qty: 0,
         sortBy: sortOptions[0],
         search: '',
      };
   }

   if (props.filter && data.query) {
      data.query.cylinders = props.options.cylinders;

      if (props.options.active_filter) {
         data.query.key_qty = props.options.active_filter.key_qty;

         if (props.options.active_filter.attributes) {
            data.query.attributes = props.options.active_filter.attributes;
         }
         if (props.options.active_filter.brand) {
            data.query.brand = props.options.active_filter.brand;
         }
         if (props.options.active_filter.rating) {
            data.query.rating = props.options.active_filter.rating;
         }
         if (props.options.active_filter.skg) {
            data.query.skg = props.options.active_filter.skg;
         }
         if (props.options.active_filter.color) {
            data.query.color = props.options.active_filter.color;
         }
         if (Number(props.options.active_filter.key_qty) > 0) {
            keysSelected.value = true;
         }

         shouldWatch.value = true;
      }

      filtered.value = true;
   } else if ((props.category.id == 1 || props.category.catalog_id == 1) && wizardCompleted) {
      add();
   }

   data.filteredCounters = props.counters;
   data.filterable = props.options.filterable;
   data.cylinderOptions = props.options.cylinder_options_default;

   updateCounters();

   setTimeout(() => {
      shouldWatch.value = true;
   }, 100);
});
</script>
